// The operator's own account: password and availability.
// Route: /profile

import { useState } from "react";

import { http } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Confirm,
  Field,
  Spinner,
  Toggle,
  useToast,
} from "../ui";
import { errMessage } from "./shared";

// ============================================================ profile

export function ProfileView() {
  const { me, refresh } = useSession();
  const toast = useToast();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [changing, setChanging] = useState(false);
  const [availabilityBusy, setAvailabilityBusy] = useState(false);

  const changePassword = async () => {
    if (next !== confirm) {
      toast.push("The new passwords do not match", "critical");
      return;
    }
    if (next.length < 6) {
      toast.push("The new password must be at least 6 characters", "critical");
      return;
    }
    setChanging(true);
    try {
      await http.put("/me/password", { current_password: current, new_password: next });
      toast.push("Password changed", "good");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.push(errMessage(err, "Could not change the password"), "critical");
    } finally {
      setChanging(false);
    }
  };

  const setAvailability = async (available: boolean) => {
    setAvailabilityBusy(true);
    try {
      const res = await http.put<{ transfers_to_queue?: number }>("/me/availability", {
        is_available: available,
      });
      const returned = res.transfers_to_queue ?? 0;
      toast.push(
        available
          ? "You are available"
          : returned > 0
            ? `You are away. ${returned} transfers went back to the queue.`
            : "You are away",
        "good"
      );
      await refresh();
    } catch (err) {
      toast.push(errMessage(err, "Could not update your availability"), "critical");
    } finally {
      setAvailabilityBusy(false);
    }
  };

  const reveal = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    shown: boolean,
    toggle: () => void,
    hint?: string
  ) => (
    <Field label={label} hint={hint}>
      <div className="row">
        <input
          type={shown ? "text" : "password"}
          value={value}
          autoComplete={label === "Current password" ? "current-password" : "new-password"}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          className="btn icon sm ghost"
          onClick={toggle}
          aria-label={shown ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
        >
          {shown ? "◎" : "◉"}
        </button>
      </div>
    </Field>
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Profile</h1>
          <div className="sub">Your account, your password and whether you are taking chats.</div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Account</h3>
        </div>
        <div className="card-body">
          <dl className="kv">
            <dt>Name</dt>
            <dd>{me?.full_name || <span className="muted">—</span>}</dd>
            <dt>Email</dt>
            <dd>{me?.email || <span className="muted">—</span>}</dd>
            <dt>Role</dt>
            <dd style={{ textTransform: "capitalize" }}>
              {me?.role?.name || <span className="muted">—</span>}
            </dd>
            <dt>Organisation</dt>
            <dd>
              <code className="mono">{me?.organization_id || "—"}</code>
            </dd>
          </dl>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Availability</h3>
        </div>
        <div className="card-body">
          <div className="setting">
            <div className="text">
              <span className="strong">Available for chats</span>
              <p>
                Going away hands your active transfers back to the queue so no conversation stalls
                while you are gone.
              </p>
            </div>
            <div className="row">
              {availabilityBusy && <Spinner label="Updating availability" />}
              <Toggle
                checked={me?.is_available ?? false}
                disabled={availabilityBusy}
                onChange={(v) => void setAvailability(v)}
                ariaLabel="Available for chats"
              />
              <Badge tone={me?.is_available ? "good" : "warning"}>
                {me?.is_available ? "Available" : "Away"}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Change password</h3>
        </div>
        <div className="card-body">
          {reveal("Current password", current, setCurrent, showCurrent, () => setShowCurrent((v) => !v))}
          {reveal(
            "New password",
            next,
            setNext,
            showNext,
            () => setShowNext((v) => !v),
            "At least 6 characters."
          )}
          {reveal("Confirm new password", confirm, setConfirm, showConfirm, () =>
            setShowConfirm((v) => !v)
          )}
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button
              className="btn primary"
              onClick={() => void changePassword()}
              disabled={changing || !current || !next}
            >
              {changing && <Spinner label="Saving" />}
              Change password
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
